import { useId, useRef, useState } from 'react';
import { autodetectHeadings, renderAnnouncementHtml } from '@kopechka/contracts';
import { deleteAdminAnnouncement } from '../api/endpoints.js';
import {
  TELEGRAM_STATUS_LABEL,
  createAdminAnnouncementWithTelegram,
  listAdminAnnouncementsWithTelegram,
  retryAnnouncementTelegram,
  telegramRetryErrorText,
  updateAdminAnnouncementWithTelegram,
  type AdminAnnouncementListWithTelegram,
  type AnnouncementBodyWithTelegram,
  type AnnouncementTelegramState,
  type WireAdminAnnouncementWithTelegram,
} from '../api/announcements-telegram.js';
import type { ApiClientOptions, ApiResult } from '../api/client.js';
import { useApiMutation, useApiQuery, useVisiblePolling } from '../api/hooks.js';
import { useCan } from '../session/can.js';
import {
  Badge,
  Banner,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  ErrorState,
  Field,
  Icon,
  Input,
  LoadingState,
  PageHead,
  Select,
  TBody,
  TD,
  THead,
  TH,
  TRow,
  Table,
} from '../ui/index.js';
import type { BadgeTone } from '../ui/index.js';

const SEVERITIES = ['info', 'warning', 'critical', 'release', 'maintenance'] as const;

function severityTone(s: string): BadgeTone {
  if (s === 'release') return 'success';
  if (s === 'warning' || s === 'maintenance') return 'warn';
  if (s === 'critical') return 'danger';
  return 'neutral';
}

/** ISO timestamp → `<input type="datetime-local">` value (LOCAL YYYY-MM-DDTHH:mm). */
function isoToLocalInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** While a channel copy is queued the list re-reads itself (visible tab only). */
const TELEGRAM_PENDING_POLL_MS = 10_000;

/**
 * `/announcements` — Duyurular. Create / list / edit / delete / activate-
 * deactivate platform announcements. Active announcements broadcast to ALL
 * customers (incl. new members) on the /dashboard/notifications page + the bell
 * badge. Gated on `announcement:manage` (admin + superadmin).
 *
 * 2026-09-23 — "Telegram kanalında da yayınla": the bot also posts the
 * announcement to the Telegram channel (default ON for new ones); editing
 * edits the channel message, deactivating / deleting removes it. The server
 * does that in the background, so each row shows the channel state
 * (Kanalda yayında / Bekliyor / Hata / Silindi) and failed rows get
 * "Tekrar dene".
 */
export function AnnouncementsPage(): JSX.Element {
  const canManage = useCan('announcement:manage');
  const list = useApiQuery<AdminAnnouncementListWithTelegram>((o) => listAdminAnnouncementsWithTelegram(o), []);
  const telegramAvailable = list.kind === 'ok' && list.data.telegram?.available === true;
  const hasPendingTelegram =
    list.kind === 'ok' && list.data.rows.some((r) => r.telegram?.status === 'pending');
  useVisiblePolling(() => list.reload(), TELEGRAM_PENDING_POLL_MS, canManage && hasPendingTelegram);

  return (
    <div>
      <PageHead
        crumb={<>Sistem <Icon.ChevronRight size={11} /> Duyurular</>}
        title="Duyurular"
        subtitle="Platform duyuruları — aktif olanlar tüm müşterilere (yeni üyeler dahil) Bildirimler & Duyurular sayfasında ve zil rozetinde görünür."
        actions={
          <Button variant="secondary" icon={<Icon.Refresh size={14} />} onClick={() => list.reload()}>
            Yenile
          </Button>
        }
      />

      {!canManage ? <ErrorState reason="capability_missing:announcement:manage" /> : null}

      {canManage ? (
        <>
          <Card flush>
            <CardHeader title="Yeni duyuru" />
            <CardBody>
              <AnnouncementForm
                submitLabel="Yayınla"
                submitIcon={<Icon.Send size={14} />}
                clearOnSuccess
                telegramAvailable={telegramAvailable}
                onSuccess={() => list.reload()}
                runner={(o, body) => createAdminAnnouncementWithTelegram(o, body)}
              />
            </CardBody>
          </Card>

          {list.kind === 'loading' ? <LoadingState /> : null}
          {list.kind === 'error' ? <ErrorState reason={list.reason} onRetry={list.reload} /> : null}
          {list.kind === 'ok' && list.data.rows.length === 0 ? (
            <Card><EmptyState label="Henüz duyuru yok." hint="Yukarıdan ilk duyuruyu oluştur." /></Card>
          ) : null}
          {list.kind === 'ok' && list.data.rows.length > 0 ? (
            <Card flush>
              <CardHeader title={`Duyurular — ${list.data.rows.length}`} />
              <CardBody tight style={{ padding: 0 }}>
                <Table>
                  <THead>
                    <TRow>
                      <TH>Başlık</TH>
                      <TH>Önem</TH>
                      <TH>Durum</TH>
                      <TH>Telegram</TH>
                      <TH>Yayın</TH>
                      <TH>Oluşturan</TH>
                      <TH></TH>
                    </TRow>
                  </THead>
                  <TBody>
                    {list.data.rows.map((a) => (
                      <AnnouncementRow
                        key={a.id}
                        row={a}
                        telegramAvailable={telegramAvailable}
                        onChanged={() => list.reload()}
                      />
                    ))}
                  </TBody>
                </Table>
              </CardBody>
            </Card>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

interface AnnouncementFormInitial {
  readonly title: string;
  readonly body: string;
  readonly severity: string;
  readonly actionUrl: string | null;
  readonly expiresAt: string | null;
  readonly postToTelegram: boolean;
}

/**
 * Shared rich-markdown authoring form — used for BOTH create (top of page) and
 * inline edit (expanded row). Owns its own editor state + mutation; the parent
 * passes the API call via `runner`. `clearOnSuccess` resets the fields after a
 * successful create; edit keeps the values and lets the parent collapse the row.
 */
function AnnouncementForm({
  initial,
  submitLabel,
  submitIcon,
  clearOnSuccess = false,
  telegramAvailable,
  onSuccess,
  onCancel,
  runner,
}: {
  readonly initial?: AnnouncementFormInitial;
  readonly submitLabel: string;
  readonly submitIcon?: JSX.Element;
  readonly clearOnSuccess?: boolean;
  /** Channel feature on (server `telegram.available`); off → checkbox disabled, not sent. */
  readonly telegramAvailable: boolean;
  readonly onSuccess?: () => void;
  readonly onCancel?: () => void;
  readonly runner: (
    o: ApiClientOptions,
    body: AnnouncementBodyWithTelegram,
  ) => Promise<ApiResult<{ announcement: WireAdminAnnouncementWithTelegram }>>;
}): JSX.Element {
  const [title, setTitle] = useState(initial?.title ?? '');
  const [body, setBody] = useState(initial?.body ?? '');
  const [severity, setSeverity] = useState<string>(initial?.severity ?? 'info');
  const [actionUrl, setActionUrl] = useState(initial?.actionUrl ?? '');
  const [expiresAt, setExpiresAt] = useState(isoToLocalInput(initial?.expiresAt ?? null));
  // New announcements go to the channel by default; an edit starts from the row.
  const [postToTelegram, setPostToTelegram] = useState<boolean>(initial?.postToTelegram ?? true);
  const telegramBoxId = useId();
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);

  // Copy-paste → auto-detect headings (# / ## / ### + standalone short lines → ##)
  // and insert the markdown at the cursor. The body is stored as markdown; the
  // live preview beside the editor renders it (sanitized) exactly like the public page.
  function handleBodyPaste(e: React.ClipboardEvent<HTMLTextAreaElement>): void {
    const pasted = e.clipboardData.getData('text');
    if (!pasted) return;
    e.preventDefault();
    const md = autodetectHeadings(pasted);
    const ta = e.currentTarget;
    const start = ta.selectionStart ?? body.length;
    const end = ta.selectionEnd ?? body.length;
    const next = body.slice(0, start) + md + body.slice(end);
    setBody(next);
    requestAnimationFrame(() => {
      const pos = start + md.length;
      if (bodyRef.current) {
        bodyRef.current.focus();
        bodyRef.current.selectionStart = pos;
        bodyRef.current.selectionEnd = pos;
      }
    });
  }

  // Toolbar: prepend a markdown prefix to each selected line (headings/lists) —
  // reliable headings even when paste-autodetect can't (e.g. heading not blank-isolated).
  function prefixLines(prefix: string): void {
    const ta = bodyRef.current;
    if (!ta) return;
    const lineStart = body.lastIndexOf('\n', ta.selectionStart - 1) + 1;
    const nlAfter = body.indexOf('\n', ta.selectionEnd);
    const blockEnd = nlAfter === -1 ? body.length : nlAfter;
    const block = body.slice(lineStart, blockEnd) || 'Başlık';
    const newBlock = block
      .split('\n')
      .map((l) => (l.startsWith(prefix) ? l : prefix + l.replace(/^(#{1,3}\s+|[-*+]\s+)/, '')))
      .join('\n');
    const next = body.slice(0, lineStart) + newBlock + body.slice(blockEnd);
    setBody(next);
    requestAnimationFrame(() => {
      if (!bodyRef.current) return;
      bodyRef.current.focus();
      bodyRef.current.selectionStart = lineStart;
      bodyRef.current.selectionEnd = lineStart + newBlock.length;
    });
  }
  // Toolbar: wrap the selection (bold/italic).
  function wrapSelection(token: string): void {
    const ta = bodyRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const sel = body.slice(start, end) || 'metin';
    const replacement = `${token}${sel}${token}`;
    const next = body.slice(0, start) + replacement + body.slice(end);
    setBody(next);
    requestAnimationFrame(() => {
      if (!bodyRef.current) return;
      bodyRef.current.focus();
      bodyRef.current.selectionStart = start + token.length;
      bodyRef.current.selectionEnd = start + token.length + sel.length;
    });
  }

  const mut = useApiMutation((o, payload: AnnouncementBodyWithTelegram) => runner(o, payload));

  const valid = title.trim().length > 0 && body.trim().length > 0;

  async function submit(): Promise<void> {
    if (!valid) return;
    const payload: AnnouncementBodyWithTelegram = {
      title: title.trim(),
      body,
      severity,
      actionUrl: actionUrl.trim() || null,
      expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
      ...(telegramAvailable ? { postToTelegram } : {}),
    };
    const s = await mut.run(payload);
    if (s.kind === 'ok') {
      if (clearOnSuccess) {
        setTitle('');
        setBody('');
        setSeverity('info');
        setActionUrl('');
        setExpiresAt('');
        setPostToTelegram(true);
      }
      onSuccess?.();
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <Field label="Başlık" htmlFor="ann-title">
        <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} />
      </Field>
      <Field
        label="İçerik (Markdown)"
        help="# → H1, ## → H2, ### → H3. Düz metin yapıştırınca boş satırla ayrılmış kısa başlıklar otomatik H2 olur; emin olmak için satırı seçip H1/H2/H3 butonuna bas. Sağdaki önizlemede başlıklar büyük görünür."
      >
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
          <Button variant="secondary" size="sm" onClick={() => prefixLines('# ')}>H1</Button>
          <Button variant="secondary" size="sm" onClick={() => prefixLines('## ')}>H2</Button>
          <Button variant="secondary" size="sm" onClick={() => prefixLines('### ')}>H3</Button>
          <Button variant="secondary" size="sm" onClick={() => wrapSelection('**')}><strong>K</strong></Button>
          <Button variant="secondary" size="sm" onClick={() => wrapSelection('*')}><em>İ</em></Button>
          <Button variant="secondary" size="sm" onClick={() => prefixLines('- ')}>• Liste</Button>
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'stretch' }}>
          <textarea
            ref={bodyRef}
            className="input"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            onPaste={handleBodyPaste}
            placeholder={'# Ana Başlık\n\n## Bölüm\n\nParagraf metni...'}
            style={{
              flex: '1 1 380px',
              resize: 'vertical',
              minHeight: 340,
              fontFamily: 'var(--font-mono, ui-monospace, monospace)',
              fontSize: 13,
              lineHeight: 1.55,
            }}
          />
          <div
            className="announcement-content"
            data-testid="ann-preview"
            aria-label="Önizleme"
            style={{
              flex: '1 1 380px',
              minHeight: 340,
              maxHeight: 520,
              overflowY: 'auto',
              padding: '10px 14px',
              border: '1px solid var(--border, rgba(127,127,127,0.25))',
              borderRadius: 8,
              background: 'var(--surface-2, rgba(127,127,127,0.06))',
            }}
            // eslint-disable-next-line react/no-danger -- sanitized by renderAnnouncementHtml (escape-first; allowlisted tags only)
            dangerouslySetInnerHTML={{
              __html:
                renderAnnouncementHtml(body) ||
                '<p style="opacity:.45;margin:0">Önizleme — yazdıkça / yapıştırdıkça başlıklar burada büyük görünür</p>',
            }}
          />
        </div>
      </Field>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <Field label="Önem" htmlFor="ann-sev">
          <Select value={severity} onChange={(e) => setSeverity(e.target.value)} style={{ width: 180 }}>
            {SEVERITIES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </Select>
        </Field>
        <Field label="Bitiş (opsiyonel)" htmlFor="ann-exp">
          <Input type="datetime-local" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
        </Field>
      </div>
      <Field label="Bağlantı (opsiyonel)" htmlFor="ann-url">
        <Input value={actionUrl} onChange={(e) => setActionUrl(e.target.value)} placeholder="https://…" />
      </Field>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <label htmlFor={telegramBoxId} style={{ display: 'inline-flex', gap: 8, alignItems: 'center', cursor: telegramAvailable ? 'pointer' : 'not-allowed' }}>
          <input
            id={telegramBoxId}
            type="checkbox"
            checked={telegramAvailable && postToTelegram}
            disabled={!telegramAvailable}
            onChange={(e) => setPostToTelegram(e.target.checked)}
            style={{ width: 'auto' }}
          />
          <span>Telegram kanalında da yayınla</span>
        </label>
        <span className="t-xs" style={{ opacity: 0.7 }}>
          {telegramAvailable
            ? 'Bot duyuruyu Telegram kanalına da gönderir; düzenleme kanal mesajını günceller, pasifleştirme / silme kanaldan kaldırır.'
            : 'Telegram kanal yayını şu an kapalı (sunucuda TELEGRAM_ANNOUNCE_ENABLED).'}
        </span>
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <Button
          variant="primary"
          disabled={!valid}
          loading={mut.state.kind === 'running'}
          onClick={() => void submit()}
          icon={submitIcon}
        >
          {submitLabel}
        </Button>
        {onCancel ? (
          <Button variant="secondary" onClick={onCancel}>
            İptal
          </Button>
        ) : null}
        {mut.state.kind === 'error' ? (
          <Banner tone="danger">{mut.state.reason}</Banner>
        ) : null}
      </div>
    </div>
  );
}

function telegramTone(status: AnnouncementTelegramState['status']): BadgeTone {
  if (status === 'posted') return 'success';
  if (status === 'pending') return 'info';
  if (status === 'failed') return 'danger';
  return 'neutral';
}

/** Tooltip text for a channel status chip (error details for pending/failed). */
function telegramTooltip(tg: AnnouncementTelegramState): string {
  if (tg.status === 'failed') return tg.error ? `Telegram hatası: ${tg.error}` : 'Telegram hatası';
  if (tg.status === 'pending') {
    return tg.error
      ? `Kanala gönderim sırada; son deneme başarısız, otomatik tekrar denenecek: ${tg.error}`
      : 'Kanala gönderim sırada';
  }
  if (tg.status === 'posted') {
    return tg.postedAt ? `Kanalda yayında (${new Date(tg.postedAt).toLocaleString()})` : 'Kanalda yayında';
  }
  if (tg.status === 'deleted') return 'Kanal mesajı silindi';
  return 'Telegram kanalında yayınlanmadı';
}

function TelegramStatusCell({
  row,
  telegramAvailable,
  onChanged,
}: {
  readonly row: WireAdminAnnouncementWithTelegram;
  readonly telegramAvailable: boolean;
  readonly onChanged: () => void;
}): JSX.Element {
  const retryMut = useApiMutation((o, _input: null) => retryAnnouncementTelegram(o, row.id));
  const tg = row.telegram;
  if (!tg || tg.status === 'skipped') {
    return (
      <span className="t-xs" style={{ opacity: 0.55 }} title={telegramTooltip(tg ?? { enabled: false, status: 'skipped', messageId: null, error: null, postedAt: null })}>
        —
      </span>
    );
  }
  async function retry(): Promise<void> {
    const s = await retryMut.run(null);
    if (s.kind === 'ok') onChanged();
  }
  const tooltip = telegramTooltip(tg);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
      <Badge tone={telegramTone(tg.status)} dot title={tooltip} aria-label={`Telegram: ${TELEGRAM_STATUS_LABEL[tg.status]}. ${tooltip}`}>
        {TELEGRAM_STATUS_LABEL[tg.status]}
      </Badge>
      {tg.status === 'failed' && telegramAvailable ? (
        <Button
          variant="secondary"
          size="sm"
          loading={retryMut.state.kind === 'running'}
          onClick={() => void retry()}
          icon={<Icon.Refresh size={12} />}
        >
          Tekrar dene
        </Button>
      ) : null}
      {retryMut.state.kind === 'error' ? (
        <Banner tone="danger">{telegramRetryErrorText(retryMut.state.reason)}</Banner>
      ) : null}
    </div>
  );
}

function AnnouncementRow({
  row,
  telegramAvailable,
  onChanged,
}: {
  readonly row: WireAdminAnnouncementWithTelegram;
  readonly telegramAvailable: boolean;
  readonly onChanged: () => void;
}): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const toggleMut = useApiMutation((o, next: boolean) =>
    updateAdminAnnouncementWithTelegram(o, row.id, { isActive: next }),
  );
  const deleteMut = useApiMutation((o, _input: null) => deleteAdminAnnouncement(o, row.id));

  async function toggle(): Promise<void> {
    const s = await toggleMut.run(!row.isActive);
    if (s.kind === 'ok') onChanged();
  }

  async function remove(): Promise<void> {
    const s = await deleteMut.run(null);
    if (s.kind === 'ok') {
      setConfirmingDelete(false);
      onChanged();
    }
  }

  return (
    <>
      <TRow>
        <TD style={{ maxWidth: 320 }}>{row.title}</TD>
        <TD><Badge tone={severityTone(row.severity)} dot>{row.severity}</Badge></TD>
        <TD>
          {row.isActive ? (
            <Badge tone="success" dot>aktif</Badge>
          ) : (
            <Badge tone="neutral" dot>pasif</Badge>
          )}
        </TD>
        <TD>
          <TelegramStatusCell row={row} telegramAvailable={telegramAvailable} onChanged={onChanged} />
        </TD>
        <TD className="t-mono t-xs">{new Date(row.publishedAt).toLocaleString()}</TD>
        <TD className="t-mono t-xs">{row.createdBy ?? '—'}</TD>
        <TD className="text-right">
          <div style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setEditing((v) => !v)}
              icon={<Icon.Pencil size={13} />}
            >
              {editing ? 'Kapat' : 'Düzenle'}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              loading={toggleMut.state.kind === 'running'}
              onClick={() => void toggle()}
            >
              {row.isActive ? 'Pasifleştir' : 'Aktifleştir'}
            </Button>
            {confirmingDelete ? (
              <>
                <Button
                  variant="danger"
                  size="sm"
                  loading={deleteMut.state.kind === 'running'}
                  onClick={() => void remove()}
                >
                  Evet, sil
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setConfirmingDelete(false)}
                >
                  Vazgeç
                </Button>
              </>
            ) : (
              <Button
                variant="danger"
                size="sm"
                onClick={() => setConfirmingDelete(true)}
                icon={<Icon.Trash size={13} />}
              >
                Sil
              </Button>
            )}
          </div>
          {deleteMut.state.kind === 'error' ? (
            <div style={{ marginTop: 6 }}><Banner tone="danger">{deleteMut.state.reason}</Banner></div>
          ) : null}
        </TD>
      </TRow>
      {editing ? (
        <TRow>
          <TD colSpan={7} style={{ background: 'var(--surface-2, rgba(127,127,127,0.04))' }}>
            <AnnouncementForm
              initial={{
                title: row.title,
                body: row.body,
                severity: row.severity,
                actionUrl: row.actionUrl,
                expiresAt: row.expiresAt,
                postToTelegram: row.telegram?.enabled ?? false,
              }}
              submitLabel="Kaydet"
              submitIcon={<Icon.Check size={14} />}
              telegramAvailable={telegramAvailable}
              onCancel={() => setEditing(false)}
              onSuccess={() => {
                setEditing(false);
                onChanged();
              }}
              runner={(o, body) => updateAdminAnnouncementWithTelegram(o, row.id, body)}
            />
          </TD>
        </TRow>
      ) : null}
    </>
  );
}
